// Co-Approval Service
// Token-based shared link for third-party song approval

import { db, functions } from './firebase';
import {
  collection,
  doc,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { generateVerificationToken } from './events';
import { pickLatestSession } from '../utils/shareLinks';

// ============================================
// TYPES
// ============================================

export interface CoApprovalSession {
  id: string;
  eventId: string;
  djId: string;
  token: string;
  status: 'active' | 'revoked';
  createdAt: string;
  email?: string | null;
  alertsEnabled?: boolean;
  linkEmailStatus?: CoApproverLinkEmailStatus | null;
}

export type CoApproverLinkEmailStatus = 'sent' | 'skipped' | 'failed';

export interface CoApprovalLinkState {
  sessionId: string;
  hasEmail: boolean;
  alertsEnabled: boolean;
}

export interface SavedCoApproverEmail {
  email: string | null;
  linkEmailStatus: CoApproverLinkEmailStatus | null;
}

const COLLECTION = 'coApprovalSessions';

// ============================================
// CRUD OPERATIONS
// ============================================

export async function createCoApprovalSession(
  eventId: string,
  djId: string
): Promise<{ sessionId: string; token: string }> {
  const token = generateVerificationToken();
  const docRef = await addDoc(collection(db, COLLECTION), {
    eventId,
    djId,
    token,
    status: 'active',
    createdAt: new Date().toISOString(),
  });
  return { sessionId: docRef.id, token };
}

export async function getCoApprovalSession(
  eventId: string,
  djId: string
): Promise<CoApprovalSession | null> {
  const q = query(
    collection(db, COLLECTION),
    where('eventId', '==', eventId),
    where('djId', '==', djId)
  );
  const snap = await getDocs(q);
  const sessions = snap.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }) as CoApprovalSession);
  return pickLatestSession(sessions, true);
}

export async function validateCoApprovalLink(eventId: string, token: string): Promise<CoApprovalLinkState> {
  const callable = httpsCallable<{ kind: 'coApproval'; eventId: string; token: string }, CoApprovalLinkState>(
    functions,
    'validateShareLink'
  );
  const result = await callable({ kind: 'coApproval', eventId, token });
  return result.data;
}

export async function saveCoApproverEmail(eventId: string, email: string | null): Promise<SavedCoApproverEmail> {
  const callable = httpsCallable<{ eventId: string; email: string | null }, SavedCoApproverEmail>(
    functions,
    'saveCoApproverEmail'
  );
  const result = await callable({ eventId, email });
  return result.data;
}

export async function setCoApprovalAlerts(eventId: string, token: string, enabled: boolean): Promise<boolean> {
  const callable = httpsCallable<{ eventId: string; token: string; enabled: boolean }, { alertsEnabled: boolean }>(
    functions,
    'setCoApprovalAlerts'
  );
  const result = await callable({ eventId, token, enabled });
  return result.data.alertsEnabled;
}

export interface CoApprovalDecisionParams {
  eventId: string;
  token: string;
  requestId: string;
  decision: 'approve' | 'reject';
  reason?: string;
}

export async function decideCoApproval(params: CoApprovalDecisionParams): Promise<void> {
  const callable = httpsCallable<CoApprovalDecisionParams, { ok: boolean }>(functions, 'decideCoApproval');
  await callable(params);
}

export async function deleteCoApprovalSession(sessionId: string): Promise<void> {
  const ref = doc(db, COLLECTION, sessionId);
  await deleteDoc(ref);
}

export async function revokeCoApprovalSession(sessionId: string): Promise<void> {
  const ref = doc(db, COLLECTION, sessionId);
  await updateDoc(ref, { status: 'revoked' });
}

// ============================================
// HELPERS
// ============================================

export function generateCoApprovalQrUrl(eventId: string, token: string): string {
  const url = `${window.location.origin}/events/${eventId}/co-approval?token=${token}`;
  return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(url)}`;
}

export function getCoApprovalPageUrl(eventId: string, token: string): string {
  return `${window.location.origin}/events/${eventId}/co-approval?token=${token}`;
}
